import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, User, Clock, PackageX } from 'lucide-react'
import { publicApi } from '../../services/api'
import Card from '../../components/ui/Card'

// Mapa fixo de status -> label pro destinatário final (BUG-61)
const statusConfig = {
  aguardando_saida: { label: 'Aguardando saída', color: 'bg-gray-100 text-gray-700' },
  em_rota: { label: 'A caminho', color: 'bg-blue-100 text-blue-700' },
  entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  nao_entregue: { label: 'Não foi entregue', color: 'bg-red-100 text-red-700' },
}

// Página pública (sem login) de rastreio pelo destinatário final, a partir do
// link enviado pelo vendedor via WhatsApp. Sem MainLayout/Sidebar de
// propósito - mesmo padrão do DriverLinkView (BUG-15) e PinConfirmView
// (BUG-37): tela isolada, pensada pra abrir direto no navegador do celular.
export default function TrackingView() {
  const { code } = useParams()

  const { data: delivery, isLoading, isError } = useQuery({
    queryKey: ['tracking', code],
    queryFn: async () => {
      const response = await publicApi.get(`/api/tracking/${code}`)
      return response.data.data.delivery
    },
    retry: false,
    // TanStack Query v5: o callback recebe só `query`, não `(data, query)`
    // como na v4 - o dado atual vem de query.state.data. Para de dar
    // polling quando a entrega chega num status final.
    refetchInterval: (query) => {
      // Erro precisa vir antes do status terminal: sem isso, data fica
      // undefined e cai no else, e o polling nunca para (BUG-67).
      if (query.state.status === 'error') return false
      const status = query.state.data?.status
      return status === 'entregue' || status === 'nao_entregue' ? false : 30000
    },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // O backend não distingue código inválido de entrega inexistente - os dois
  // casos voltam como 404, de propósito. Mesma tela genérica das outras
  // páginas públicas (DriverLinkView, PinConfirmView) - não vazar o motivo.
  if (isError || !delivery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <PackageX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Este link não está mais disponível</p>
          <p className="text-sm text-gray-500 mt-1">Fale com quem te enviou o link pra conseguir um novo.</p>
        </div>
      </div>
    )
  }

  const statusInfo = statusConfig[delivery.status] || statusConfig.aguardando_saida

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <h1 className="text-lg font-semibold text-gray-900">Rastreio — {delivery.vendorName}</h1>
      </div>

      <div className="p-4 space-y-3">
        <Card padding="sm">
          <div className="flex justify-between items-start gap-2 mb-2">
            <p className="text-sm text-gray-500 flex items-start gap-1">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{delivery.location}</span>
            </p>
            <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>

          {delivery.driverFirstName && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <User className="w-3.5 h-3.5" />
              Entregador: {delivery.driverFirstName}
            </p>
          )}

          {delivery.position && (
            <p className="text-sm text-gray-500 mt-1">
              {delivery.position.current}ª parada de {delivery.position.total}
            </p>
          )}

          {delivery.status === 'em_rota' && delivery.estimatedArrival && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <Clock className="w-3.5 h-3.5" />
              Previsão: {delivery.estimatedArrival.substring(0, 5)}
            </p>
          )}

          {delivery.status === 'entregue' && delivery.deliveredAt && (
            <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
              <Clock className="w-3.5 h-3.5" />
              Entregue às {new Date(delivery.deliveredAt).toLocaleString('pt-BR')}
            </p>
          )}
        </Card>
      </div>
    </div>
  )
}
